import { useState, useEffect } from 'react'

/**
 * Debounces a value after a specified delay.
 * Useful for deferring expensive operations (like API calls) until typing pauses.
 *
 * @template T The type of the value being debounced
 * @param value The current value to debounce
 * @param delay The debounce delay in milliseconds (default 300ms)
 * @returns The debounced value
 */
export function useDebouncedValue<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => clearTimeout(handler)
  }, [value, delay])

  return debouncedValue
}
